import React, { memo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { TranscriptionViewer } from './transcription';
import { 
  MeetingHeader, 
  MeetingTabPanel, 
  MeetingEditor, 
  MeetingTabs 
} from './meeting';
import { TABS_CONFIG, EDITOR_DATA_TYPES } from './meeting/constants';
import { idKey, transcriptionKey, keypointsKey, isSummarizedKey } from './MeetingsKeysList';


const RightPanel = memo(({
  meeting,
  tabValue,
  onTabChange,
  onMeetingUpdate,
  onMeetingDelete,
  onMeetingShare,
  onEditorSelectionChange,
  onModalStateChange
}) => {
  // Meeting data is now managed in context - optimized with memoization


  // Create tab content rendering function (removed memoization to ensure updates)
  // Note: This must be called before any early returns (React Hooks rule)
  const renderTabContent = useCallback((tabIndex) => {
    if (!meeting) return null;
    const tabConfig = TABS_CONFIG[tabIndex];
    
    
    // Show message if data is missing for transcription
    // Note: keypoints now handled by MeetingEditor like summary

    if (tabIndex === 2 && !meeting[transcriptionKey]) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          height="200px"
        >
          <Typography variant="body2" color="textSecondary">
            Transcription not available for this meeting.
          </Typography>
        </Box>
      );
    }
    
    switch (tabConfig.type) {
      case 'summary':
        return (
          <MeetingEditor
            key={`summary-${meeting[idKey]}-${meeting[isSummarizedKey]}`}
            meeting={meeting}
            dataType={EDITOR_DATA_TYPES.SUMMARY}
            onMeetingUpdate={onMeetingUpdate}
            onEditorSelectionChange={onEditorSelectionChange}
          />
        );
      case 'keypoints':
        return (
          <MeetingEditor
            key={`keypoints-${meeting[idKey]}-${meeting[isSummarizedKey]}`}
            meeting={meeting}
            dataType={EDITOR_DATA_TYPES.KEYPOINTS}
            onMeetingUpdate={onMeetingUpdate}
            onEditorSelectionChange={onEditorSelectionChange}
          />
        );
      case 'transcription':
        return (
          <TranscriptionViewer 
            key={`transcription-${meeting[idKey]}-${meeting[isSummarizedKey]}`}
            meeting={meeting} 
            meeting_id={meeting[idKey]}
          />
        );
      default:
        return null;
    }
  }, [meeting, onMeetingUpdate, onEditorSelectionChange, onModalStateChange]);

  if (!meeting) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }} id="95bro">
      {/* Elegant Title and Date Header */}
      <MeetingHeader
                    meeting={meeting}
        onMeetingUpdate={onMeetingUpdate}
        onMeetingDelete={onMeetingDelete}
        onMeetingShare={onMeetingShare}
        onModalStateChange={onModalStateChange}
      />

      {/* Tabs header */}
      <MeetingTabs
        tabValue={tabValue}
        onTabChange={onTabChange}
                    meeting={meeting}
      />

      {/* Tab content */}
      {TABS_CONFIG.map((tab) => (
        <MeetingTabPanel 
          key={tab.index}
          value={tabValue} 
          index={tab.index}
        >
          {renderTabContent(tab.index)}
        </MeetingTabPanel>
      ))}
    </Box>
  );
});

RightPanel.displayName = 'RightPanel';

export default RightPanel; 